import { CheckIcon, XMarkIcon, CheckCircleIcon } from "@heroicons/react/24/solid";
import type { FeedPreviewEpisode } from "@/api/client";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

interface SelectableEpisodeCardProps {
  episode: FeedPreviewEpisode;
  isSelected: boolean;
  onToggle: () => void;
  disabled?: boolean;
  hasTranscript?: boolean;
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "Duration unknown";
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return remainingMins > 0 ? `${hrs}h ${remainingMins}m` : `${hrs}h`;
}

function estimateTokens(seconds: number | null): string {
  if (seconds === null) return "";
  // ~100 tokens per minute of audio
  const tokens = Math.round((seconds / 60) * 100);
  if (tokens >= 1000) {
    return `~${(tokens / 1000).toFixed(1)}k tokens`;
  }
  return `~${tokens} tokens`;
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + "...";
}

export default function SelectableEpisodeCard({
  episode,
  isSelected,
  onToggle,
  disabled = false,
  hasTranscript = false,
}: SelectableEpisodeCardProps) {
  const tokenEstimate = estimateTokens(episode.duration_seconds);

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={cn(
        "w-full text-left p-4 rounded-xl transition-all",
        theme.borders.neubrutalism,
        isSelected
          ? cn(theme.colors.primaryOrange, theme.shadows.neubrutalismSm)
          : cn("bg-white dark:bg-gray-800", theme.shadows.neubrutalism, "hover:-translate-y-0.5"),
        disabled && "opacity-50 cursor-not-allowed hover:translate-y-0"
      )}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox indicator */}
        <div
          className={cn(
            "flex-shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center mt-0.5",
            isSelected
              ? "bg-gray-900 dark:bg-white border-gray-900 dark:border-white"
              : "border-gray-400 dark:border-gray-500"
          )}
        >
          {isSelected && (
            <CheckIcon className="w-4 h-4 text-white dark:text-gray-900" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-2">
            {episode.title}
          </h3>

          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
            {truncateText(episode.description || "No description", 120)}
          </p>

          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
            <span>{formatDuration(episode.duration_seconds)}</span>
            {tokenEstimate && (
              <>
                <span>·</span>
                <span>{tokenEstimate}</span>
              </>
            )}
            {hasTranscript && (
              <>
                <span>·</span>
                <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                  <CheckCircleIcon className="w-3.5 h-3.5" />
                  Ready
                </span>
              </>
            )}
          </div>
        </div>

        {/* Remove button for selected items */}
        {isSelected && (
          <div
            className="flex-shrink-0 p-1 rounded-lg hover:bg-gray-900/10 dark:hover:bg-white/10"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          >
            <XMarkIcon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
          </div>
        )}
      </div>
    </button>
  );
}
